import React, { useMemo } from 'react';
import { Box, Button, Typography } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { CollectionInvoiceDocument, CollectionInvoicePdf } from '../model';
import { formatDollar, formatOptionalDate } from '../utils/format';
import { useInvoicePdfUpload } from '../hooks/useInvoicePdfUpload';
import { useInvoicePdfDelete } from '../hooks/useInvoicePdfDelete';
import { groupInvoicesByDepartment } from '../utils/groupInvoices';
import DepartmentGroupedInvoiceSections from '../../shared/invoices/DepartmentGroupedInvoiceSections';
import InvoicePdfCell from '../../shared/invoices/InvoicePdfCell';

interface Props {
  documents: CollectionInvoiceDocument[];
  location: string;
  acctNo: string;
  onUploaded: (applyTo: string, pdf: CollectionInvoicePdf) => void;
  onDeleted: (applyTo: string, pdfId: number) => void;
  noCard?: boolean;
}

const buildColumns = (
  triggerUpload: (applyTo: string) => void,
  uploadingApplyTo: string | null,
  canUpload: boolean,
  deletePdf: (applyTo: string, pdfId: number, filename: string) => void,
  deletingPdfId: number | null,
) => [
  {
    name: 'Invoice #',
    selector: (row: CollectionInvoiceDocument) => row.apply_to,
    width: '130px',
  },
  {
    name: 'Invoice Date',
    selector: (row: CollectionInvoiceDocument) => formatOptionalDate(row.invoice_date),
    width: '130px',
  },
  {
    name: 'Due Date',
    selector: (row: CollectionInvoiceDocument) => formatOptionalDate(row.due_date),
    width: '130px',
  },
  {
    name: 'Amount',
    selector: (row: CollectionInvoiceDocument) => row.amount,
    cell: (row: CollectionInvoiceDocument) => <Typography variant="body2">{formatDollar(row.amount)}</Typography>,
    right: true,
    width: '170px',
  },
  {
    name: 'PDF',
    selector: (row: CollectionInvoiceDocument) => row.pdf?.filename ?? '',
    grow: 2,
    cell: (row: CollectionInvoiceDocument) =>
      row.pdf ? (
        <InvoicePdfCell pdf={row.pdf} applyTo={row.apply_to} deletingPdfId={deletingPdfId} onDelete={deletePdf} />
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<UploadFileIcon />}
            disabled={!canUpload || uploadingApplyTo === row.apply_to}
            onClick={() => triggerUpload(row.apply_to)}
          >
            {uploadingApplyTo === row.apply_to ? 'Uploading…' : 'Upload Invoice'}
          </Button>
        </Box>
      ),
  },
];

const ClientInvoicesTable: React.FC<Props> = ({
  documents,
  location,
  acctNo,
  onUploaded,
  onDeleted,
  noCard = false,
}) => {
  const { fileInputRef, triggerUpload, handleFileSelected, uploadingApplyTo, canUpload } = useInvoicePdfUpload({
    location,
    acctNo,
    onUploaded: ({ applyTo, pdf }) => onUploaded(applyTo, pdf),
  });
  const { deletePdf, deletingPdfId } = useInvoicePdfDelete({ onDeleted });

  const columns = useMemo(
    () => buildColumns(triggerUpload, uploadingApplyTo, canUpload, deletePdf, deletingPdfId),
    [triggerUpload, uploadingApplyTo, canUpload, deletePdf, deletingPdfId],
  );

  const groups = useMemo(() => groupInvoicesByDepartment(documents), [documents]);
  const totalAmount = useMemo(() => documents.reduce((sum, invoice) => sum + invoice.amount, 0), [documents]);
  const namedDepartmentCount = useMemo(() => groups.filter((group) => !group.isUngrouped).length, [groups]);
  const hasUngroupedGroup = useMemo(() => groups.some((group) => group.isUngrouped), [groups]);

  const summaryText = (
    <>
      <Box component="span" sx={{ color: 'text.primary', fontWeight: 700 }}>
        {documents.length}
      </Box>{' '}
      collection {documents.length === 1 ? 'invoice' : 'invoices'} in{' '}
      <Box component="span" sx={{ color: 'text.primary', fontWeight: 700 }}>
        {namedDepartmentCount}
      </Box>{' '}
      {namedDepartmentCount === 1 ? 'department' : 'departments'}
      {hasUngroupedGroup ? ' + ungrouped' : ''} with past due of{' '}
      <Box component="span" sx={{ color: 'error.main', fontWeight: 700 }}>
        {formatDollar(totalAmount)}
      </Box>
    </>
  );

  const content =
    documents.length === 0 ? (
      <Box sx={{ p: 3, color: 'text.secondary' }}>No past-due invoices.</Box>
    ) : (
      <DepartmentGroupedInvoiceSections
        groups={groups}
        columns={columns}
        summaryText={summaryText}
        emptyMessage="No past-due invoices."
      />
    );

  return (
    <>
      <input ref={fileInputRef} type="file" accept="application/pdf,.pdf" hidden onChange={handleFileSelected} />
      {noCard ? <Box sx={{ p: 2 }}>{content}</Box> : content}
    </>
  );
};

export default ClientInvoicesTable;
